import type { CSSProperties } from 'react';
import { domainIcon, hhmm, isTeams } from '@jobfair/shared';
import { domainColor, hashIdx, heDate } from '../lib/format';
import { useDialog } from '../lib/hooks';
import { useTrack } from '../lib/trackerContext';
import type { LiveState, Phase, Site, Unit } from '../types';
import { ExtLink } from './common';
import { Icon } from './Icon';

interface Props {
  u: Unit;
  site: Site;
  st: LiveState;
  ph: Phase;
  fav: boolean;
  onFav: (id: string) => void;
  onClose: () => void;
  onKeyword: (k: string) => void;
}

function Emblem({ u }: { u: Unit }) {
  return (
    <span className="emblem lg">
      {u.symbol ? <img src={u.symbol} alt="" /> : <Icon n={domainIcon(u.domain)} s={30} />}
    </span>
  );
}

export function UnitDrawer({ u, site, st, ph, fav, onFav, onClose, onKeyword }: Props) {
  const ref = useDialog<HTMLDivElement>(onClose);
  const track = useTrack();
  const r1 = ph.valid ? hhmm(ph.R[0]!.start) : st.rounds[0].start;

  let join;
  if (ph.teamsOpen && isTeams(u.teams)) {
    join = (
      <div className="join-box">
        <ExtLink
          className="btn btn-teams btn-lg"
          href={u.teams}
          label={`מפגש Teams של ${u.name}`}
          onOpen={() => track('join_click', { unitId: u.id, value: 'drawer' })}
        >
          <Icon n="video" s={20} />
          הצטרפות למפגש
        </ExtLink>
        <small>
          המפגש ייפתח ב-Microsoft Teams בלשונית חדשה. אותו קישור משמש את כל שלושת הסבבים, ואפשר
          לחזור לכאן בכל רגע.
        </small>
      </div>
    );
  } else if (ph.teamsOpen) {
    join = (
      <div className="callout info">
        <Icon n="info" />
        <span>ליחידה זו אין כרגע מפגש זמין. אפשר לעיין במידע ובחומרים שלה.</span>
      </div>
    );
  } else if (ph.state === 'ended') {
    join = (
      <div className="callout info">
        <Icon n="check" />
        <span>המפגשים הסתיימו. הגשת מועמדות להתמחות מתבצעת במערכת קודקס.</span>
      </div>
    );
  } else {
    join = (
      <div className="callout info">
        <Icon n="clock" />
        <span>
          המפגשים עם היחידה ייפתחו ב-{heDate(st.date, { day: 'numeric', month: 'long' })} בשעה{' '}
          <b className="num">{r1}</b>.
        </span>
      </div>
    );
  }

  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div
        className="drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="d-title"
        ref={ref}
        tabIndex={-1}
        style={{ '--ac': domainColor(site.domains, u.domain) } as CSSProperties}
      >
        <div className="drawer-head">
          <button
            className="btn btn-sm btn-ghost"
            aria-pressed={fav ? 'true' : 'false'}
            onClick={() => onFav(u.id)}
            style={
              fav
                ? { color: 'var(--fg)', borderColor: 'var(--yellow)', background: 'var(--warn-bg)' }
                : undefined
            }
          >
            <Icon n="star" s={16} fill={fav} />
            {fav ? 'שמור במועדפים' : 'שמירה במועדפים'}
          </button>
          <button
            className="icon-btn"
            onClick={onClose}
            aria-label="סגירה וחזרה לרשימה"
            data-autofocus
          >
            <Icon n="close" />
          </button>
        </div>
        <div className="drawer-body">
          {u.logo ? (
            <div className="d-logo">
              <img src={u.logo} alt={'הלוגו של ' + u.name} />
            </div>
          ) : (
            <div className={'d-band backdrop p' + hashIdx(u.id)} aria-hidden="true">
              <Emblem u={u} />
            </div>
          )}
          <div className="d-titlewrap">
            <h2 className="d-title" id="d-title">
              {u.name}
            </h2>
            {(u.nameAr || u.nameEn) && (
              <div className="d-alt">
                {u.nameAr && (
                  <span lang="ar" dir="rtl">
                    {u.nameAr}
                  </span>
                )}
                {u.nameEn && (
                  <span lang="en" dir="ltr">
                    {u.nameEn}
                  </span>
                )}
              </div>
            )}
            {(u.domain || u.region || u.booth) && (
              <div className="d-meta">
                {u.booth && <span className="mchip num">דוכן {u.booth}</span>}
                {u.domain && (
                  <span className="mchip">
                    <Icon n={domainIcon(u.domain)} s={15} />
                    {u.domain}
                  </span>
                )}
                {u.region && (
                  <span>
                    <Icon n="pin" s={15} />
                    {u.region}
                  </span>
                )}
              </div>
            )}
          </div>
          {join}
          {u.description && (
            <section>
              <div className="sec-label">על היחידה</div>
              <div className="desc">
                {u.description
                  .split('\n')
                  .filter(Boolean)
                  .map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
              </div>
            </section>
          )}
          {(u.pdfs.length > 0 || u.youtube) && (
            <section>
              <div className="sec-label">חומרים</div>
              <div className="res-list">
                {u.youtube && (
                  <ExtLink
                    className="res"
                    href={u.youtube}
                    label="הסרטון ב-YouTube"
                    onOpen={() => track('video_open', { unitId: u.id })}
                  >
                    <span className="ri vid">
                      <Icon n="play" fill />
                    </span>
                    <span>
                      <b>סרטון היכרות עם היחידה</b>
                      <small>YouTube · נפתח בלשונית חדשה</small>
                    </span>
                    <span className="ext">
                      <Icon n="ext" s={16} />
                    </span>
                  </ExtLink>
                )}
                {u.pdfs.map((p, i) => (
                  <ExtLink
                    key={i}
                    className="res"
                    href={p.url}
                    label="המסמך"
                    onOpen={() => track('pdf_open', { unitId: u.id, value: p.title })}
                  >
                    <span className="ri pdf">
                      <Icon n="file" />
                    </span>
                    <span>
                      <b>{p.title || 'מסמך מידע'}</b>
                      <small>PDF · נפתח בלשונית חדשה</small>
                    </span>
                    <span className="ext">
                      <Icon n="ext" s={16} />
                    </span>
                  </ExtLink>
                ))}
              </div>
            </section>
          )}
          {u.keywords.length > 0 && (
            <section>
              <div className="sec-label">נושאים</div>
              <div className="kw">
                {u.keywords.map((k) => (
                  <button key={k} onClick={() => onKeyword(k)} title="חיפוש יחידות נוספות בנושא">
                    {k}
                  </button>
                ))}
              </div>
            </section>
          )}
          <p className="d-foot">הגשת מועמדות להתמחות אינה מתבצעת באתר היריד, אלא במערכת קודקס.</p>
        </div>
      </div>
    </>
  );
}
